'use client';

import React, { useCallback, useMemo, useState } from 'react';

import Image from 'next/image';

import notFoundIcon from '@/assets/icons/table-dead-state-icon.svg';
import profilePlaceholder from '@/assets/images/avatarPlaceholder.png';
import RightIcon from '@/assets/images/right-btn-arrow-green.svg';
import SearchIcon from '@/assets/images/search-md.svg';
import Button from '@/components/V2/Button';
import ButtonLink from '@/components/V2/ButtonLink';
import DeadState from '@/components/V2/DeadState';
import Spinner from '@/components/V2/PetProfile/Spinner';
import LoadingSpinner from '@/components/V2/Spinner';
import TextInput from '@/components/V2/TextInput';
import { useSearchPetProMutation } from '@/store/api/authentication';
import { PetPro } from '@/types/Signup';

interface PetProSearchProps {
  onPetProSelected?: (petPro: PetPro) => void;
  onContinueWithout?: () => void;
  isSubmittingPetPro?: boolean;
  isSubmittingWithout?: boolean;
}

// Avatar component for PetPro
const PetProAvatar: React.FC<{ customerID: number; fullName: string }> = ({ customerID, fullName }) => {
  const [imageError, setImageError] = useState(false);
  const [imageLoading, setImageLoading] = useState(true);

  const avatarUrl = useMemo(
    () =>
      typeof window !== 'undefined'
        ? `${window.location.origin}/internal_api/profiles/avatar/${customerID}/default/true`
        : '',
    [customerID]
  );

  return (
    <div className="v2-h-[114px] v2-w-[114px] v2-rounded-full v2-border v2-border-black v2-border-opacity-[8%] v2-flex v2-items-center v2-justify-center v2-bg-gray-100 v2-overflow-hidden">
      {imageLoading && !imageError && (
        <div className="v2-absolute v2-flex v2-items-center v2-justify-center">
          <span className="v2-text-gray-400 v2-text-2xl"></span>
        </div>
      )}
      <Image
        src={imageError ? profilePlaceholder : avatarUrl}
        alt={fullName}
        width={114}
        height={114}
        className="v2-rounded-full v2-object-cover"
        onLoad={() => setImageLoading(false)}
        onError={() => {
          setImageError(true);
          setImageLoading(false);
        }}
      />
    </div>
  );
};

const PetProSearch: React.FC<PetProSearchProps> = ({
  onPetProSelected = () => {},
  onContinueWithout = () => {},
  isSubmittingPetPro = false,
  isSubmittingWithout = false,
}) => {
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState<PetPro[]>([]);
  const [isSearching, setIsSearching] = useState(false);
  const [hasSearched, setHasSearched] = useState(false);
  const [selectedPetProId, setSelectedPetProId] = useState<number | null>(null);

  const [searchPetProMutation] = useSearchPetProMutation();

  const handleSearch = useCallback(async () => {
    if (!searchQuery.trim()) return;

    setIsSearching(true);
    setHasSearched(true);

    try {
      const response = await searchPetProMutation({ query: searchQuery.trim() }).unwrap();
      if (response.success) {
        setSearchResults(response.data);
      }
    } catch (error) {
      console.error('Search failed:', error);
      setSearchResults([]);
    } finally {
      setIsSearching(false);
    }
  }, [searchQuery, searchPetProMutation]);

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      handleSearch();
    }
  };

  const handleClearSearch = () => {
    setSearchQuery('');
    setSearchResults([]);
    setHasSearched(false);
  };

  console.log('searchResults => ', searchResults);

  return (
    <div className="">
      <div className="v2-p-4 v2-bg-[#F9FAFB] v2-border-[#EAECF0] v2-border v2-mb-[19px]">
        <div className="v2-mb-4 v2-flex md:v2-flex-row sm:v2-flex-col v2-gap-3 xs:v2-flex-col v2-items-start md:v2-items-center v2-justify-between">
          <h2 className="v2-font-sweet-sans-pro-medium v2-font-medium v2-text-4xl md:v2-text-3xl">Find a PetPro</h2>
          <ButtonLink
            scheme={'primary'}
            variant={'outlined'}
            className="!v2-p-0 v2-border-0 v2-flex v2-items-center v2-gap-1 !v2-w-fit !v2-bg-transparent"
            onClick={onContinueWithout}
            disabled={isSubmittingWithout}
          >
            <p className="v2-underline">Continue without choosing a petPro</p>
            {isSubmittingWithout ? <Spinner height={16} /> : <Image src={RightIcon} alt="" />}
          </ButtonLink>
        </div>
        <div className="">
          <p className="v2-font-sweet-sans-pro v2-font-normal v2-text-sm v2-text-[#344054] v2-mb-[6px]">
            Search by PetPro ID, First/LastName, Email, Company, Phone (Numbers Only), Website
          </p>
          <div className="v2-relative v2-flex v2-items-center">
            <TextInput
              type={'text'}
              placeholder="Select team member"
              inputClassName="v2-font-sweet-sans-pro v2-font-normal placeholder:v2-text-[#667085]"
              value={searchQuery}
              onChange={e => setSearchQuery(e.target.value)}
              onKeyPress={handleKeyPress}
            />
            <button
              type="button"
              onClick={handleSearch}
              className="v2-absolute v2-right-3 v2-top-[9px] v2-cursor-pointer"
              aria-label="Search PetPros"
            >
              <Image src={SearchIcon} alt="" />
            </button>
          </div>
        </div>
      </div>
      {isSearching ? (
        <div className="v2-border v2-border-[#EAECF0] v2-shadow-md v2-p-6 v2-rounded-xs v2-flex v2-justify-center v2-items-center v2-h-48">
          <LoadingSpinner className="!v2-h-10 !v2-w-10" />
        </div>
      ) : hasSearched ? (
        searchResults.length > 0 ? (
          <div className="v2-border v2-border-[#EAECF0] v2-shadow-md v2-p-6 v2-rounded-xs v2-grid v2-grid-cols-3 md:v2-grid-cols-3 sm:v2-grid-cols-2 xs:v2-grid-cols-1 v2-gap-6">
            {searchResults.map(petPro => (
              <div
                key={petPro.customerID}
                className="v2-p-5 v2-shadow-sm v2-border v2-border-[#EAECF0] v2-flex v2-items-center v2-gap-4 v2-flex-col"
              >
                <PetProAvatar customerID={petPro.customerID} fullName={petPro.fullName} />
                <h2 className="v2-font-sweet-sans-pro-medium v2-font-medium v2-text-xl v2-text-center">
                  {petPro.fullName}
                </h2>
                <p className="v2-font-sweet-sans-pro v2-font-normalmedium v2-text-sm">ID# {petPro.customerID}</p>
                <p className="v2-font-sweet-sans-pro v2-font-normalmedium v2-text-sm v2-text-center">
                  {petPro.mainCity}, {petPro.mainState}
                </p>
                <Button
                  btnSize="md"
                  scheme="primary"
                  variant="filled"
                  type="button"
                  className="v2-text-sm v2-font-sweet-sans-pro-medium v2-font-medium v2-flex v2-items-center v2-justify-center v2-gap-2"
                  onClick={() => {
                    setSelectedPetProId(petPro.customerID);
                    onPetProSelected(petPro);
                  }}
                  disabled={isSubmittingPetPro}
                >
                  {isSubmittingPetPro && selectedPetProId === petPro.customerID ? 'Loading...' : 'Choose'}
                  {isSubmittingPetPro && selectedPetProId === petPro.customerID && <Spinner height={16} />}
                </Button>
              </div>
            ))}
          </div>
        ) : (
          <div className="v2-border v2-border-[#EAECF0] v2-shadow-md v2-p-6 v2-rounded-xs">
            <DeadState
              icon={notFoundIcon}
              title="No PetPros Found"
              message="We couldn't find any PetPros matching your search. Try different search terms."
              className="!v2-h-auto !v2-w-full"
              contentWrapperClassName="!v2-pt-0"
            />
          </div>
        )
      ) : null}
    </div>
  );
};

export default PetProSearch;
